import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useScriptDashboardOverview } from "@/hooks/useTenantDashboardService";
import useAuthStore from "@/store/useAuthStore";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";

interface RepScriptsTabProps {
  rep?: any;
  tenantId?: string;
  dateFilter?: string;
}

function formatDate(isoStr?: string): string {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(isoStr?: string): string {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function getTypeColor(typeStr?: string, isDark: boolean = true): string {
  const lower = (typeStr || "").toLowerCase();
  if (lower === "call") return isDark ? "#38bdf8" : "#0284c7";       // Blue
  if (lower === "demo") return isDark ? "#a78bfa" : "#7c3aed";       // Purple
  if (lower === "negotiation" || lower === "nego") return isDark ? "#f97316" : "#ea580c"; // Orange
  return isDark ? "#4ade80" : "#16a34a";                             // Green (Objection)
}

function formatSectionTitle(typeStr: string, idx: number): string {
  const clean = typeStr ? typeStr.replace(/_/g, " ") : `Section ${idx + 1}`;
  return `${idx + 1}. ${clean.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}`;
}

// ─── Score Ring Component ──────────────────────────────────────────────────
const ScoreRing = ({ score, size = 70 }: { score: number; size?: number }) => {
  const { isDark, colors } = useDashboardTheme();
  const s = Math.min(Math.round(score), 100);
  const r = size / 2 - 7;
  const circ = 2 * Math.PI * r;
  const dash = (s / 100) * circ;

  let color = isDark ? "#4ADE80" : "#5a9d50";
  let text = "Completed";
  if (s < 50) {
    color = "#F87171";
    text = "Needs Focus";
  } else if (s < 80) {
    color = "#FBBF24";
    text = "Good";
  }

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={colors.ringTrack}
          strokeWidth="6"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.5s ease" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 800, color: colors.text }}>{s}%</span>
        <span style={{ fontSize: 9, color: colors.textMuted, marginTop: -2 }}>
          {text}
        </span>
      </div>
    </div>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────────

// ─── Icon Avatar ──────────────────────────────────────────────────────────────
const IconAvatar = ({ children, color }: { children: React.ReactNode; color: string }) => (
  <div
    style={{
      width: 44,
      height: 44,
      borderRadius: 14,
      background: `${color}15`,
      border: `1px solid ${color}35`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </div>
);

const ScriptIcon = (c: string) => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const CalendarIcon = (c: string) => (
  <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 7V3m8 4V3M4 11h16M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const LayersIcon = (c: string) => (
  <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
  </svg>
);

const RepScriptsTab: React.FC<RepScriptsTabProps> = ({ rep, tenantId: tenantIdProp }) => {
  const { isDark, colors } = useDashboardTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantid } = useAuthStore();
  const effectiveTenantId = tenantIdProp || localStorage.getItem("tenantId") || tenantid || "";

  const { data: overviewRes, isLoading } = useScriptDashboardOverview(effectiveTenantId);

  const overviewData = overviewRes?.data || {};
  const summary = overviewData.summary || {
    totalScriptsCreated: 0,
    totalAssignments: 0,
    totalCompletedAssignments: 0,
    completionPercentage: 0,
    totalPracticeAttempts: 0,
    totalCompletedSections: 0,
    avgPracticeScore: 0,
  };

  const rawActivities = overviewData.recentPracticeActivities || [];

  const targetRepId = rep?.userId || rep?.id || rep?._id;
  const targetRepName = rep?.name || rep?.fullName || rep?.userName;

  const repActivities = useMemo(() => {
    if (!targetRepId) return rawActivities;
    return rawActivities.filter(
      (act: any) =>
        act.repId === targetRepId ||
        act.userId === targetRepId ||
        act.assignedToId === targetRepId ||
        (targetRepName && act.repName && act.repName.toLowerCase() === targetRepName.toLowerCase())
    );
  }, [rawActivities, targetRepId, targetRepName]);

  const repSummary = useMemo(() => {
    if (!targetRepId) return summary;
    const repStat = (overviewData.repScriptStats || []).find(
      (st: any) => st.userId === targetRepId || st.repId === targetRepId
    );
    if (repStat) {
      return {
        totalAssignments: repStat.assignedCount || 0,
        totalCompletedAssignments: repStat.completedCount || 0,
        totalPracticeAttempts: repStat.practiceAttemptCount || 0,
        completionPercentage: repStat.assignedCount > 0 ? Math.round((repStat.completedCount / repStat.assignedCount) * 100) : 0,
      };
    }
    const totalAssignments = repActivities.length;
    const totalCompletedAssignments = repActivities.filter((act: any) => {
      const completed = (act.sections || []).filter((s: any) => s.isCompleted).length;
      return completed > 0 && completed === (act.sections || []).length;
    }).length;
    return {
      totalAssignments,
      totalCompletedAssignments,
      totalPracticeAttempts: repActivities.length,
      completionPercentage: totalAssignments > 0 ? Math.round((totalCompletedAssignments / totalAssignments) * 100) : 0,
    };
  }, [overviewData.repScriptStats, targetRepId, repActivities, summary]);

  const userName =
    rep?.name ||
    overviewData.repScriptStats?.[0]?.userName ||
    repActivities?.[0]?.repName ||
    "Bhagyashree Rajput user";

  // Filter State
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const filteredActivities = useMemo(() => {
    return repActivities.filter((act: any) => {
      // 1. Script Type Filter
      if (selectedType !== "all") {
        const typeStr = (act.scriptType || "").toLowerCase();
        if (selectedType === "nego" && typeStr !== "nego" && typeStr !== "negotiation") {
          return false;
        }
        if (selectedType !== "nego" && typeStr !== selectedType) {
          return false;
        }
      }

      // 2. Status Filter
      const completedCount = (act.sections || []).filter((s: any) => s.isCompleted).length;
      const isAllCompleted = completedCount > 0 && completedCount === (act.sections || []).length;
      if (selectedStatus === "completed" && !isAllCompleted) return false;
      if (selectedStatus === "in_progress" && (completedCount === 0 || isAllCompleted)) return false;
      if (selectedStatus === "not_started" && completedCount > 0) return false;

      // 3. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const titleMatch = (act.scriptTitle || "").toLowerCase().includes(q);
        const repMatch = (act.repName || "").toLowerCase().includes(q);
        if (!titleMatch && !repMatch) return false;
      }

      return true;
    });
  }, [repActivities, selectedType, selectedStatus, searchQuery]);

  const eyebrowStyleDynamic: React.CSSProperties = {
    fontSize: 11,
    textTransform: "uppercase",
    color: colors.textMuted,
    letterSpacing: "0.14em",
    fontWeight: 700,
    margin: "0 0 6px",
    display: "block",
  };

  return (
    <div style={{ color: colors.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* ─── Top Header ─── */}
      <div
        style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 20,
          marginBottom: 24,
          padding: "22px 26px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 20,
          boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: colors.text }}>
              {userName}
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: 999,
                background: isDark ? "rgba(52,211,153,0.12)" : "rgba(34,197,94,0.1)",
                color: isDark ? "#34d399" : "#15803D",
                border: `1px solid ${isDark ? "rgba(52,211,153,0.3)" : "rgba(34,197,94,0.25)"}`,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: isDark ? "#34d399" : "#15803D" }} />
              Active
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 14, color: colors.textMuted }}>
            <span style={{ color: colors.text, fontWeight: 600 }}>
              {repSummary.totalAssignments ?? 0} Scripts Assigned
            </span>
            <span style={{ color: colors.border }}>·</span>
            <span style={{ color: isDark ? "#4ADE80" : "#15803D", fontWeight: 600 }}>
              {repSummary.totalCompletedAssignments ?? 0} Completed
            </span>
            <span style={{ color: colors.border }}>·</span>
            <span style={{ color: isDark ? "#FACC15" : "#D97706", fontWeight: 600 }}>
              {repSummary.totalPracticeAttempts ?? 0} Practice Attempts
            </span>
          </div>
        </div>
      </div>

      {/* ─── Filter Bar (Script Type, Status, Search) ─── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 14,
          padding: "16px 20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {/* Filter 1: Script Type Dropdown */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: colors.textMuted, textTransform: "uppercase", fontWeight: 700 }}>
              Script Type
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              style={{
                background: colors.inputBg,
                border: `1px solid ${colors.inputBorder}`,
                borderRadius: 8,
                padding: "8px 14px",
                color: colors.inputText,
                fontSize: 13,
                outline: "none",
                cursor: "pointer",
                minWidth: 140,
              }}
            >
              <option value="all">All Types</option>
              <option value="call">Call</option>
              <option value="demo">Demo</option>
              <option value="nego">Negotiation</option>
              <option value="objection">Objection</option>
            </select>
          </div>

          {/* Filter 2: Status Dropdown */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: colors.textMuted, textTransform: "uppercase", fontWeight: 700 }}>
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                background: colors.inputBg,
                border: `1px solid ${colors.inputBorder}`,
                borderRadius: 8,
                padding: "8px 14px",
                color: colors.inputText,
                fontSize: 13,
                outline: "none",
                cursor: "pointer",
                minWidth: 140,
              }}
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="not_started">Not Started</option>
            </select>
          </div>
        </div>

        {/* Search Input Box */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 11, color: colors.textMuted, textTransform: "uppercase", fontWeight: 700 }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search script title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              background: colors.inputBg,
              border: `1px solid ${colors.inputBorder}`,
              borderRadius: 8,
              padding: "8px 14px",
              color: colors.inputText,
              fontSize: 13,
              outline: "none",
              minWidth: 220,
            }}
          />
        </div>
      </div>

      {/* ─── Script List Cards ─── */}
      {isLoading ? (
        <div style={{ padding: 60, textAlign: "center", color: colors.textMuted }}>
          Loading scripts...
        </div>
      ) : filteredActivities.length === 0 ? (
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 14,
            padding: 60,
            textAlign: "center",
            color: colors.textMuted,
            fontSize: 14,
          }}
        >
          No scripts found matching the selected filters.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {filteredActivities.map((act: any) => {
            const typeColor = getTypeColor(act.scriptType, isDark);
            const sections = act.sections || [];
            const completedSectionsCount = sections.filter((s: any) => s.isCompleted).length;
            const totalSectionsCount = sections.length || 4;
            const pct = Math.round((completedSectionsCount / totalSectionsCount) * 100);
            const maxAttempt = sections.reduce((acc: number, s: any) => Math.max(acc, s.attemptCount || 0), 0);

            return (
              <div
                key={act.assignmentId || act.scriptMetaId}
                style={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderLeft: `3px solid ${typeColor}`,
                  borderRadius: 18,
                  padding: "24px 28px",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {/* ── Top Row ── */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 24,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Script Title & Identity Block */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 14,
                      flex: "1 1 260px",
                      minWidth: 220,
                    }}
                  >
                    <IconAvatar color={typeColor}>{ScriptIcon(typeColor)}</IconAvatar>
                    <div style={{ minWidth: 0 }}>
                      <span style={eyebrowStyleDynamic}>Script Overview</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                        <h3
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: colors.text,
                            margin: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 240,
                          }}
                        >
                          {act.scriptTitle || "Untitled Script"}
                        </h3>

                        {/* Type Badge */}
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            padding: "3px 10px",
                            borderRadius: 999,
                            background: `${typeColor}1f`,
                            color: typeColor,
                            border: `1px solid ${typeColor}40`,
                            textTransform: "capitalize",
                          }}
                        >
                          {act.scriptType || "Call"}
                        </span>
                      </div>

                      <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>
                        Rep: <span style={{ color: colors.text, fontWeight: 600 }}>{act.repName}</span>
                        {act.repEmail && (
                          <span style={{ color: colors.textMuted, marginLeft: 6 }}>
                            ({act.repEmail})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Timestamp Block */}
                  <div style={{ flex: "0 0 140px" }}>
                    <span style={eyebrowStyleDynamic}>Timestamp</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {CalendarIcon(typeColor)}
                      <div>
                        <p style={{ fontSize: 14, fontWeight: 700, color: colors.text, margin: 0 }}>
                          {formatDate(act.createdAt)}
                        </p>
                        <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>
                          {formatTime(act.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Practice Progress Block */}
                  <div style={{ flex: "0 0 140px" }}>
                    <span style={eyebrowStyleDynamic}>Sections & Attempts</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {LayersIcon(typeColor)}
                      <div>
                        <p style={{ fontSize: 14, fontWeight: 700, color: colors.text, margin: 0 }}>
                          {completedSectionsCount}/{totalSectionsCount} Completed
                        </p>
                        <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>
                          Attempts: {maxAttempt}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Score / Completion Ring */}
                  <div
                    style={{
                      flex: "0 0 110px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ ...eyebrowStyleDynamic, textAlign: "center" }}>Progress Score</span>
                    <ScoreRing score={pct} size={70} />
                  </div>

                  {/* View Details Action Button */}
                  <div style={{ flex: "0 0 auto", marginLeft: "auto" }}>
                    <button
                      onClick={() => {
                        const st = (location.state as any) || {};
                        const repObj = rep || st.selectedRep || st.rep;
                        navigate(
                          `/script-details/${act.assignmentId || act.scriptMetaId}?assignedToId=${act.repId}`,
                          { state: { ...st, from: location.pathname + location.search, selectedRep: repObj, repTab: "scripts" } }
                        );
                      }}
                      style={{
                        background: `linear-gradient(135deg, ${typeColor}18, ${typeColor}08)`,
                        border: `1px solid ${typeColor}45`,
                        color: typeColor,
                        padding: "10px 18px",
                        borderRadius: 11,
                        fontSize: 13.5,
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        whiteSpace: "nowrap",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${typeColor}30, ${typeColor}15)`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${typeColor}18, ${typeColor}08)`;
                      }}
                    >
                      View Details
                      <svg
                        width="13"
                        height="13"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* ── Divider ── */}
                <div style={{ height: 1, background: colors.divider, margin: "20px 0" }} />

                {/* ── Bottom Section: Section Breakdown Chips ── */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: colors.textMuted, fontWeight: 700, textTransform: "uppercase" }}>
                    Sections:
                  </span>
                  {sections.map((sec: any, sIdx: number) => (
                    <span
                      key={sIdx}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "4px 12px",
                        borderRadius: 999,
                        background: sec.isCompleted
                          ? isDark ? "rgba(74, 222, 128, 0.12)" : "rgba(34, 197, 94, 0.12)"
                          : colors.surfaceAlt,
                        color: sec.isCompleted
                          ? isDark ? "#4ADE80" : "#15803D"
                          : colors.textMuted,
                        border: `1px solid ${
                          sec.isCompleted
                            ? isDark ? "rgba(74, 222, 128, 0.28)" : "rgba(34, 197, 94, 0.25)"
                            : colors.border
                        }`,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: sec.isCompleted
                            ? isDark ? "#4ADE80" : "#15803D"
                            : colors.textMuted,
                        }}
                      />
                      {formatSectionTitle(sec.type, sIdx)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RepScriptsTab;
